import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { ArrowLeft, ClipboardList } from 'lucide-react';
import { FormSubmissionsPanel } from '@/components/forms/FormSubmissionsPanel';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { dynamicFormsApi } from '@/lib/api/dynamic-forms';
import type { DynamicForm } from '@/types/dynamic-form';
import type { FormField } from '@/types/form-definition';

/**
 * Dedicated submissions workspace: one form metadata fetch here, then the panel loads the list.
 * Keeps the form overview page lighter (progressive disclosure).
 */
export default function FormSubmissionsPage() {
  const { formId } = useParams<{ formId: string }>();
  const [form, setForm] = useState<DynamicForm | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!formId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await dynamicFormsApi.get(formId);
      if (res.statusCode !== 200) throw new Error(res.message || 'Failed to load form');
      setForm(res.data.form);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load form');
      setForm(null);
    } finally {
      setLoading(false);
    }
  }, [formId]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <Button variant="ghost" size="sm" className="w-fit gap-2 px-0 text-muted-foreground" asChild>
        <Link to={formId ? `/f/${formId}` : '/f'}>
          <ArrowLeft className="h-4 w-4" />
          Form overview
        </Link>
      </Button>

      {loading ? (
        <div className="space-y-4">
          <Skeleton className="h-10 w-2/3" />
          <Skeleton className="h-64 w-full rounded-xl" />
        </div>
      ) : error ? (
        <Card className="border-destructive/30 bg-destructive/5">
          <CardHeader>
            <CardTitle className="text-destructive">Could not load form</CardTitle>
            <CardDescription>{error}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button variant="outline" asChild>
              <Link to="/f">All forms</Link>
            </Button>
          </CardContent>
        </Card>
      ) : form ? (
        <>
          <header className="space-y-2 border-b border-border/60 pb-6">
            <div className="flex items-center gap-2 text-muted-foreground">
              <ClipboardList className="h-4 w-4" aria-hidden />
              <span className="text-xs font-medium uppercase tracking-wider">Submissions</span>
            </div>
            <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">{form.title}</h1>
            <p className="max-w-2xl text-sm text-muted-foreground leading-relaxed">
              Filter by status, switch cards or table, export CSV, and open a row for full answers. This screen loads form
              settings once, then the list below — so the form overview page stays fast.
            </p>
            <p className="text-xs text-muted-foreground">
              {typeof form.submissionCount === 'number' ? (
                <>
                  <span className="font-medium text-foreground">{form.submissionCount}</span> saved{' '}
                  {form.submissionCount === 1 ? 'response' : 'responses'}
                  {form.lastSubmissionAt
                    ? ` · last ${formatDistanceToNow(new Date(form.lastSubmissionAt), { addSuffix: true })}`
                    : ''}
                </>
              ) : (
                'No submission summary on file.'
              )}
            </p>
          </header>

          <Card className="border-border/70 shadow-sm">
            <CardContent className="p-4 sm:p-6">
              <FormSubmissionsPanel
                formId={form._id}
                publishedVersion={form.publishedVersion}
                formFields={(form.draftDefinition?.fields ?? []) as FormField[]}
                onSubmissionsMutated={() => void load()}
              />
            </CardContent>
          </Card>
        </>
      ) : null}
    </div>
  );
}
